import React, { useEffect, useRef, useState } from 'react';
import './agency.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  copyTextToClipboard,
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getVehiclesData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import VehiclesDTO from '../../../dto/VehiclesDTO';
import VehiclesForm from './VehiclesForm';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import FilterForm from './FilterForm';
import { vehiclesAdditionalColumns, vehiclesColumns } from './vehiclesColumns';
import {
  deleteVehicles, loadVehiclesById, loadVehiclesByIdClear, loadVehiclesList,
} from '../actions';
import { AGENCY_MODULE } from '../../../configs/moduleNames';

export default function VehiclesPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);

  const store = useStore();
  const {
    vehiclesListMeta,
    loadingVehiclesList,
    vehiclesList,
    loadingCurrentVehicles,
    currentVehicles,
    loadingCreateVehicles,
    loadingEditVehiclesById,
    loadingDeleteVehicles,
  } = useSelector(getVehiclesData);
  const { formatMessage } = useIntl();

  const loadVehicles = id => {
    if (id) {
      dispatch(loadVehiclesById(id))
        .then(() => {
          setIsEdit(true);
          setShowView(false);
          setShowForm(true);
        })
        .catch(response => errorNotification('Загрузка ТС', getResponseError(response)));
    }
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[AGENCY_MODULE].vehiclesListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getClearValues(),
    };
    const p1 = dispatch(loadVehiclesList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const {
    getValues, getClearValues, control, setValue, reset,
  } = useFilterForm({}, loadData, ['device_id']);
  const did = getValues('device_id');
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => loadVehicles(getValues('device_id')), [did]);
  const DTO = new VehiclesDTO();
  const attributes = DTO.getAttributesByName();

  const handleSelectRow = item => {
    let select = item;
    if (selectedRow && selectedRow.device_id === item.device_id) {
      select = null;
    }
    setSelectedRow(select);
  };

  const handleCreate = () => {
    setIsEdit(false);
    setShowView(false);
    setShowForm(true);
    clearCurrentState();
  };

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    loadVehicles(item.device_id);
  };

  const handleDelete = item => event => {
    if (!item.device_id) {
      errorNotification('Ошибка', 'Нет ID');
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление ТС # ${item.vehicle_id}`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteVehicles(item.device_id)).then(() => {
        if (selectedRow?.device_id === item.device_id) {
          setSelectedRow(null);
        }
        loadData();
        successNotification('Удаление ТС', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление ТС', getResponseError(response));
      });
    }).catch(() => {});
    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const copyLink = item => event => {
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    copyTextToClipboard(`${baseUrl}?device_id=${item.device_id}`).then(success => {
      if (success) {
        successNotification('Копирование ссылки', 'Ссылка скопирована');
      } else {
        errorNotification('Копирование ссылки', 'Не удалось скопировать ссылку');
      }
    });
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = () => {
    setShowForm(false);
    setValue('device_id', '');
    clearCurrentState();
  };

  const clearCurrentState = () => {
    dispatch(loadVehiclesByIdClear()).catch(responseLogWithNotification);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={copyLink(item)}>
            Копировать ссылку
          </Button>
          <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
            Редактировать
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    if (selectedRow && item.device_id === selectedRow.device_id) {
      classes.push('selected');
    }
    return classes.join(' ');
  };

  return (
    <div className="page application-page">
      <Loader loading={loadingVehiclesList || loadingCurrentVehicles || loadingDeleteVehicles || loadingCreateVehicles || loadingEditVehiclesById} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.vehicles' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={attributes.updated}
            hideSmallButtons
            showArrowIcon
            usePortal
            placeholder="Дата обновления"
            changeFormat="YYYY-MM-DD"
            inputProps={{
              value: [getValues('updated_from'), getValues('updated_to')],
              onChange: e => {
                const v = e.target.value;
                setValue('updated_from', v[0]);
                setValue('updated_to', v[1]);
              },
            }}
            className="no-border"
          />
          {/* <Button variant="second" onClick={() => setOpenFilter(true)}> */}
          {/*  <Icons customIcon width={24} height={24} icon="filter" /> */}
          {/* </Button> */}
          <div className="filter-sep" />
          <Button variant="second" onClick={handleCreate}>
            <Icons
              customIcon
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
          {isOpenFilter
            ? (
              <FilterForm
                control={control}
                onHide={() => {
                  setOpenFilter(false);
                }}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                setValue={setValue}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                onClickByRow={handleSelectRow}
                onDoubleClickByRow={(item, event) => handleEdit(item)(event)}
                rowClassFormatter={rowClassFormatter}
                data={vehiclesList}
                columns={vehiclesColumns}
                additionalColumns={vehiclesAdditionalColumns}
                name="vehicles-list"
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={vehiclesListMeta} list={vehiclesList} handleChangeMeta={loadData} />
        </div>
      </div>
      {showForm ? (
        <VehiclesForm
          currentVehicles={currentVehicles}
          isEdit={isEdit}
          loadData={loadData}
          onClose={handleClose}
          loadVehicles={loadVehicles}
        />
      ) : null}
    </div>
  );
}
